import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, defineAsyncComponent, onMounted, nextTick, getCurrentInstance } from 'vue';
import { formatWithLocale, add, isEqual, isAfter, isBefore } from '@white-label-helper/date-utilities';
import { getDaysInMonth, isLastDayOfMonth, isFirstDayOfMonth } from 'date-fns';
import type { Day } from './cavu-calendar.types.d.ts';

import CavuCalendarDays from './cavu-calendar-days/cavu-calendar-days.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'cavu-calendar',
  props: {
    value: null,
    mode: { default: 'single' },
    minDate: { default: () => new Date() },
    maxDate: { default: () => add(new Date(), { years: 1 }) },
    maxRangeLength: { default: null },
    displayMonths: { default: 'one' },
    startWeekOn: { default: 'monday' },
    includeTrailingCells: { type: Boolean, default: true }
  },
  emits: ["input", "esc-pressed", "tab-pressed-on-outer"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: Date[]): void
  (e: 'esc-pressed'): void
  (e: 'tab-pressed-on-outer', event: KeyboardEvent): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: Date[], mode: 'single' | 'multiple' | 'range', minDate: Date, maxDate: Date, maxRangeLength: number | null, displayMonths: 'one' | 'two' | 'scrolling', startWeekOn: 'sunday' | 'monday', includeTrailingCells: boolean };

const IconS3 = defineAsyncComponent(
  () => import('@white-label-icon/icons-s3'),
);





// Instance proxy for translations
const proxy = getCurrentInstance()?.proxy as Vue;

const initialMonth = new Date(props.minDate);
const currentMonth = ref<Date>(initialMonth);
const selectedDates = ref<Date[]>([]);

/**
 * Localized weekday abbreviations for the scrolling mode header, ordered by the startWeekOn preference.
 */
const localizedWeekdays = computed(() => {
  const locale = proxy.$i18n.locale;
  const baseDate = new Date(Date.UTC(2026, 0, 4)); // A known Sunday
  const days = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(baseDate);
    day.setUTCDate(baseDate.getUTCDate() + i);
    return {
      abbr: formatWithLocale(day, 'eeeeee', locale),
      full: formatWithLocale(day, 'eeee', locale),
    };
  });
  return props.startWeekOn === 'sunday' ? days : [...days.slice(1), days[0]];
});

watch(() => props.value, (newValue) => {
  selectedDates.value = [...newValue];
}, { deep: true });

const isOneDayRangeSelected = computed(() => props.mode === 'range' && selectedDates.value.length === 2 && isEqual(selectedDates.value[0], selectedDates.value[1]));

const DEFAULT_MAX_RANGE_YEARS = 1;

function getMaxRangeBoundary(baseDate: Date, direction: 'forward' | 'backward'): Date {
  const rangeValue = direction === 'backward' ? -1 : 1;

  if (typeof props.maxRangeLength === 'number') {
    return add(baseDate, { days: props.maxRangeLength * rangeValue });
  }

  return add(baseDate, { years: DEFAULT_MAX_RANGE_YEARS * rangeValue });
}

function getEffectiveMaxDate(maxRangeBoundaryDate: Date): Date {
  if (!props.maxDate) {
    return maxRangeBoundaryDate;
  }

  return isBefore(props.maxDate, maxRangeBoundaryDate) ? props.maxDate : maxRangeBoundaryDate;
}

/**
 * Calculate the adjusted maximum date based on the selected start date and maxRangeLength when in range mode.
 * This ensures that when a user selects a start date, the end date options are limited to valid dates within the max range length.
 */
const adjustedMaxDate = computed(() => {
  if (props.mode !== 'range' || selectedDates.value.length !== 1) {
    return props.maxDate;
  }

  const startDate = selectedDates.value[0];
  const newMaxDate = getEffectiveMaxDate(getMaxRangeBoundary(startDate, 'forward'));
  return newMaxDate;
});

/**
 * Calculate the months to show based on the displayMonths prop.
 * - For 'two' mode, this will return the current month and the next month.
 * - For 'one' mode, it will return just the current month.
 * - For 'scrolling' mode, this is not used as all months are rendered in a scrollable list.
 */
const monthsToShow = computed<Date[]>(() => {
  if (props.displayMonths === 'two') {
    const nextMonthDate = new Date(currentMonth.value);
    nextMonthDate.setDate(1);
    nextMonthDate.setMonth(nextMonthDate.getMonth() + 1);

    return [currentMonth.value, nextMonthDate];
  }
  return [currentMonth.value];
});

/**
 * Calculate the list of months to render when in 'scrolling' display mode.
 * This generates an array of Date objects representing the first day of each month from the minDate to the maxDate (adjusted for range selection if applicable).
 */
const scrollingMonths = computed<Date[]>(() => {
  if (props.displayMonths !== 'scrolling') return [];
  // Array of Dates for the first day of each month for the next year
  const result: Date[] = [];
  const start = new Date(props.minDate.getFullYear(), props.minDate.getMonth(), 1);
  const end = new Date(adjustedMaxDate.value.getFullYear(), adjustedMaxDate.value.getMonth(), 1);

  let current = new Date(start);
  while (current <= end) {
    result.push(new Date(current));
    current.setMonth(current.getMonth() + 1);
  }

  return result;
});

/**
 * Gets the list of Day objects to render for a given month.
 * This includes information about whether each day is selected, disabled, in the current range, etc.
 * @param date The month for which to get the days.
 */
const getDays = (date: Date): Day[] => {
  const month = date.getMonth();
  const year = date.getFullYear();
  const daysInMonth = getDaysInMonth(date);

  const minDateStart = new Date(props.minDate);
  minDateStart.setHours(0, 0, 0, 0);

  const maxDateNormalized = new Date(props.maxDate);
  maxDateNormalized.setHours(0, 0, 0, 0);

  const adjustedMaxDateNormalized = new Date(adjustedMaxDate.value);
  adjustedMaxDateNormalized.setHours(0, 0, 0, 0);

  return Array.from({ length: daysInMonth }, (_, i) => {
    const date = new Date(year, month, i + 1);
    date.setHours(0, 0, 0, 0);
    const dayIsSelected = (d: Date) => selectedDates.value.some(selectedDate => isEqual(selectedDate, d));
    return {
      date: new Date(year, month, i + 1), // Return original time for display
      disabled: (() => {
        if (date < minDateStart) return true;
        if (props.mode === 'range' && selectedDates.value.length === 1) {
          const selectedDate = selectedDates.value[0];
          if (isBefore(date, selectedDate)) {
            return isBefore(date, getMaxRangeBoundary(selectedDate, 'backward'));
          } else {
            return isAfter(date, getEffectiveMaxDate(getMaxRangeBoundary(selectedDate, 'forward')));
          }
        } else if (props.mode === 'range' && selectedDates.value.length === 2) {
          return date > maxDateNormalized;
        }
        if (date > adjustedMaxDateNormalized) return true;
        return false;
      })(),
      selected: dayIsSelected(date),
      inRange: (() => {
        if (props.mode !== 'range' || selectedDates.value.length < 2) return false;
        const [start, end] = selectedDates.value;
        return date > start && date < end;
      })(),
      startRange: (() => {
        if (props.mode !== 'range' || selectedDates.value.length < 2) return false;
        const [start] = selectedDates.value;
        return isEqual(date, start);
      })(),
      endRange: (() => {
        if (props.mode !== 'range' || selectedDates.value.length < 2) return false;
        const [_, end] = selectedDates.value;
        return isEqual(date, end);
      })(),
      firstDayOfWeekOrMonth: (
        (props.startWeekOn === 'sunday' ? date.getDay() === 0 : date.getDay() === 1)
        || isFirstDayOfMonth(date)
      ),
      lastDayOfWeekOrMonth: (
        (props.startWeekOn === 'sunday' ? date.getDay() === 6 : date.getDay() === 0)
        || isLastDayOfMonth(date)
      ),
      getElement: () => {
        return document.querySelector(
          `button[data-date="${date.toISOString()}"]`
        ) as HTMLElement;
      },
      focusDay: () => {
        const dayButton = document.querySelector(
          `button[data-date="${date.toISOString()}"]`
        ) as HTMLElement;
        if (dayButton) {
          dayButton.focus();
        }
      }
    };
  });
};

/**
 * Calculate the list of Day objects to render for the current month(s) being displayed.
 * This takes into account the display mode (one, two, or scrolling months) and combines the days accordingly.
 */
const days = computed<Day[]>(() => {
  const currentMonthDays = getDays(currentMonth.value);
  if (props.displayMonths === 'one') {
    return currentMonthDays;
  } else if (props.displayMonths === 'two') {
    const nextMonthDate = new Date(currentMonth.value);
    nextMonthDate.setDate(1);
    nextMonthDate.setMonth(nextMonthDate.getMonth() + 1);
    return [ ...currentMonthDays, ...getDays(nextMonthDate)];
  } else {
    return scrollingMonths.value.flatMap(month => getDays(month));
  }
});

function isMonthBeforeMin(month: Date) {
  return (
    month.getFullYear() < props.minDate.getFullYear() ||
    (month.getFullYear() === props.minDate.getFullYear() && month.getMonth() < props.minDate.getMonth())
  );
}

function isMonthAfterMax(month: Date) {
  return (
    month.getFullYear() > adjustedMaxDate.value.getFullYear() ||
    (month.getFullYear() === adjustedMaxDate.value.getFullYear() && month.getMonth() > adjustedMaxDate.value.getMonth())
  );
}

function goToPrevMonth() {
  const d = new Date(currentMonth.value);
  d.setMonth(d.getMonth() - 1);
  if (!isMonthBeforeMin(d)) {
    currentMonth.value = d;
    nextTick(() => {
      initialiseRovingTabindex();
    });
  }
}

/**
 * Navigate to the next month, unless it would go after the maximum allowed date.
 * In 'two' month display mode, also check that the month after the next month does not go after the maximum allowed date, to ensure that there is always at least one valid month to display.
 */
function goToNextMonth() {
  const d = new Date(currentMonth.value);

  d.setDate(1);
  d.setMonth(d.getMonth() + 1);

  const monthToCheck = props.displayMonths === 'two'
    ? new Date(d.getFullYear(), d.getMonth() + 1, 1)
    : d;

  if (!isMonthAfterMax(monthToCheck)) {
    currentMonth.value = d;
    nextTick(() => {
      initialiseRovingTabindex();
    });
  }
}

const isPrevMonthDisabled = computed(() =>
  isMonthBeforeMin(new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() - 1, 1))
);

const isNextMonthDisabled = computed(() => {
  const offset = props.displayMonths === 'two' ? 2 : 1;
  return isMonthAfterMax(new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() + offset, 1));
});

/**
 * Handles date selection across all modes:
 * - single: replaces selection.
 * - multiple: toggles the date in the selection.
 * - range: first click sets start, second sets end; clicking an existing
 *   date in a two-date range restarts selection from that date.
 */
function selectDate(day: Day) {
  if (day.disabled) return;

  if (day.selected) {
    if (props.mode === 'range' && selectedDates.value.length === 1) {
      selectedDates.value.push(day.date);
    } else if (isOneDayRangeSelected.value) {
      selectedDates.value = [];
    } else if (props.mode === 'range' && selectedDates.value.length === 2) {
      selectedDates.value = [day.date];
    } else {
      selectedDates.value = [ ...selectedDates.value.filter(d => !isEqual(d, day.date)) ];
    }
    emit('input', selectedDates.value);
    return;
  }

  if (props.mode === 'single') {
    selectedDates.value = [day.date];
  } else if (props.mode === 'range') {
    if (selectedDates.value.length < 2) {
      selectedDates.value = [...selectedDates.value, day.date].sort((a, b) => a.getTime() - b.getTime());
    } else {
      selectedDates.value = [day.date];
    }
  } else if (props.mode === 'multiple') {
    selectedDates.value.push(day.date);
  }

  emit('input', selectedDates.value)
}

function handleArrowPress(event: KeyboardEvent, day: Day) {
  event.preventDefault();
  let newDate: Date | null = null;
  switch (event.key) {
    case 'ArrowUp':
      newDate = new Date(day.date);
      newDate.setDate(newDate.getDate() - 7);
      break;
    case 'ArrowDown':
      newDate = new Date(day.date);
      newDate.setDate(newDate.getDate() + 7);
      break;
    case 'ArrowLeft':
      newDate = new Date(day.date);
      newDate.setDate(newDate.getDate() - 1);
      break;
    case 'ArrowRight':
      newDate = new Date(day.date);
      newDate.setDate(newDate.getDate() + 1);
      break;
  }
  if (newDate) {
    const targetDay = days.value.find(d => isEqual(d.date, newDate));
    if (targetDay && !targetDay.disabled) {
      day.getElement()?.setAttribute('tabindex', '-1');
      targetDay.getElement()?.setAttribute('tabindex', '0');
      targetDay.focusDay();
    }
  }
}

function focusMonthNavigation() {
  const nextButton = document.querySelector('button[data-testid="calendar--next-month"]') as HTMLButtonElement | null;
  const prevButton = document.querySelector('button[data-testid="calendar--prev-month"]') as HTMLButtonElement | null;
  prevButton?.disabled
    ? nextButton?.focus()
    : prevButton?.focus();
}

function focusActiveDay() {
  const activeDay = days.value.find(day => day.getElement()?.getAttribute('tabindex') === '0');
  activeDay?.focusDay();
}

function handleNextButtonShiftTab(event: KeyboardEvent) {
  if (isPrevMonthDisabled.value) {
    event.preventDefault();
    focusActiveDay();
  }
}

function focusSameDayInPrevMonth() {
  const prevButton = document.querySelector('button[data-testid="calendar--prev-month"]') as HTMLButtonElement | null;
  const focusedDate = days.value.find(day => day.getElement().getAttribute('tabindex') === '0')?.date;
  if (!prevButton?.disabled && focusedDate) {
    const targetDate = add(focusedDate, { months: -1 });
    goToPrevMonth();
    nextTick(() => {
      const targetDay = days.value.find(d => isEqual(d.date, targetDate));
      initialiseRovingTabindex(targetDate)
      targetDay?.focusDay();
    });
  }
}

function focusSameDayInNextMonth() {
  const nextButton = document.querySelector('button[data-testid="calendar--next-month"]') as HTMLButtonElement | null;
  const focusedDate = days.value.find(day => day.getElement().getAttribute('tabindex') === '0')?.date;
  if (!nextButton?.disabled && focusedDate) {
    const targetDate = add(focusedDate, { months: 1 });
    goToNextMonth();
    nextTick(() => {
      const targetDay = days.value.find(d => isEqual(d.date, targetDate));
      initialiseRovingTabindex(targetDate)
      targetDay?.focusDay();
    });
  }
}

/**
 * Initializes the roving tabindex for keyboard navigation.
 * This sets the tabindex of all day elements to -1, except for the starting date or the first selectable date, which is set to 0 to be focusable.
 * @param startingDate the date to initially focus
 */
function initialiseRovingTabindex(startingDate?: Date) {
  const startingDay = startingDate ? days.value.find(d => isEqual(d.date, startingDate)) : null;
  const firstSelectableDay = days.value.find(d => !d.disabled);

  days.value.forEach((day) => {
    day.getElement()?.setAttribute('tabindex', '-1');
  });

  if (!startingDay || startingDay.disabled) {
    firstSelectableDay?.getElement()?.setAttribute('tabindex', '0');
    return;
  }
  startingDay.getElement()?.setAttribute('tabindex', '0');
}

onMounted(() => {
  selectedDates.value = [ ...props.value ];
  if (selectedDates.value.length > 0) {
    currentMonth.value = new Date(selectedDates.value[0]);
  }
  nextTick(() => {
    const firstSelectedDay = days.value.find(d => d.selected);
    if (firstSelectedDay) {
      firstSelectedDay.focusDay();

      if (props.displayMonths === 'scrolling') {
        firstSelectedDay.getElement()?.scrollIntoView({ block: 'center' });
      }
    } else {
      const firstEnabledDay = days.value.find(d => !d.disabled);
      firstEnabledDay?.focusDay();
    }

    initialiseRovingTabindex(firstSelectedDay?.date ?? undefined);
  })
});

return { __sfc: true,IconS3, props, emit, proxy, initialMonth, currentMonth, selectedDates, localizedWeekdays, isOneDayRangeSelected, DEFAULT_MAX_RANGE_YEARS, getMaxRangeBoundary, getEffectiveMaxDate, adjustedMaxDate, monthsToShow, scrollingMonths, getDays, days, isMonthBeforeMin, isMonthAfterMax, goToPrevMonth, goToNextMonth, isPrevMonthDisabled, isNextMonthDisabled, selectDate, handleArrowPress, focusMonthNavigation, focusActiveDay, handleNextButtonShiftTab, focusSameDayInPrevMonth, focusSameDayInNextMonth, initialiseRovingTabindex, formatWithLocale, CavuCalendarDays }
}

})